import React, { useState, useEffect, useRef } from 'react';
import { 
  TrendingUp, DollarSign, Users, Award, BarChart3, Rocket,Facebook,Instagram,Youtube,
  Cpu, Globe, Clock, ChevronRight, Menu, X, ArrowRight,
  Mail, Phone, MapPin, Linkedin, Twitter, CheckCircle2,
  Play, Star, Quote, ChevronDown, ChevronsUpDown, Check,
  TwitterIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  companyInfo, heroContent, stats, whatIsLeadGen,
  benefits, whyChooseUs, processSteps, testimonials,
  faqs, ctaContent, footerLinks, clientLogos, countryCodes
} from '../data/mock';

// Icon mapping for dynamic rendering
const iconMap = {
  TrendingUp, DollarSign, Users, Award, BarChart3, Rocket,
  Cpu, Globe, Clock
};

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const end = parseInt(value);
          const incrementTime = duration / end;
          const timer = setInterval(() => {
            start += 1;
            setCount(start);
            if (start >= end) clearInterval(timer);
          }, Math.max(incrementTime, 10));
        }
      },
      { threshold: 0.5 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [value, duration, hasAnimated]);

  return (
    <span ref={countRef}>
      {count}{suffix}
    </span>
  );
};

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: 'https://itmunch.com/', external: true },
    { label: 'Services', href: '#services' },
    { label: 'How It Works', href: '#process' },
    { label: 'Why Us', href: '#why-us' },
    { label: 'Testimonials', href: '#testimonials' },
    { label: 'FAQ', href: '#faq' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="https://itmunch.com/" className="flex items-center gap-2">
            <img 
              src={companyInfo.logo.header} 
              alt={companyInfo.name}
              className="h-10 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.external ? "_blank" : undefined}
                rel={link.external ? "noopener noreferrer" : undefined}
                className={`text-sm font-medium transition-colors hover:text-[#4AC5DB] ${
                  isScrolled ? 'text-slate-600' : 'text-slate-700'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
             <Button 
              className="bg-[#4AC5DB] hover:bg-[#3bb5cb] text-white px-6"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-slate-900" />
            ) : (
              <Menu className="w-6 h-6 text-slate-900" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 bg-white rounded-lg shadow-lg mt-2">
            <nav className="flex flex-col gap-2 px-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target={link.external ? "_blank" : undefined}
                  rel={link.external ? "noopener noreferrer" : undefined}
                  className="py-2 text-slate-600 hover:text-[#4AC5DB] font-medium"
                  onClick={() => !link.external && setIsMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <Button className="mt-4 bg-[#4AC5DB] hover:bg-[#3bb5cb] text-white">
                Get Started
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const [formData, setFormData] = useState({
    // name: '', email: '', company: '', countryCode: '\'', phone: '', message: '', optIn: false
    name: '', email: '', company: '', countryCode: '', phone: '', message: '', optIn: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);

  const [errors, setErrors] = useState({});
  const validate = () => {
  const newErrors = {};
const clearError = (field) => {
  setErrors((prev) => {
    const newErrors = { ...prev };
    delete newErrors[field];
    return newErrors;
  });
};

  // Name
  if (!formData.name.trim()) {
    newErrors.name = "Full name is required";
  }

  // Email
  if (!formData.email.trim()) {
    newErrors.email = "Work email is required";
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const blockedDomains = ["gmail.com", "gmail.co.uk", "gmail.co.in",
"yahoo.com", "yahoo.co.in", "yahoo.co.uk", "yahoo.ca", "yahoo.com.au", "yahoo.com.sg", "yahoo.co.jp",
"outlook.com", "outlook.co.uk", "outlook.co.in",
"hotmail.com", "hotmail.co.uk",
"live.com", "live.co.uk",
"msn.com",
"icloud.com", "me.com", "mac.com",
"aol.com",
"proton.me", "protonmail.com",
"gmx.com", "gmx.de", "gmx.co.uk",
"mail.com",
"zoho.com",
"yandex.com", "yandex.ru",
"tutanota.com",
"fastmail.com",
"hushmail.com",
"rediffmail.com",
"inbox.com",
"rocketmail.com"];

    const domain = formData.email.split("@")[1]?.toLowerCase();

    if (!emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    } else if (blockedDomains.includes(domain)) {
      newErrors.email = "Please use your company email address";
    }
  }

  // Company
  if (!formData.company.trim()) {
    newErrors.company = "Company name is required";
  }

  // Country Code
if (!formData.countryCode || formData.countryCode.trim() === "") {
  newErrors.countryCode = "Country code is required";
}


  // Phone
  if (!formData.phone.trim()) {
    newErrors.phone = "Phone number is required";
  } else if (!/^\d+$/.test(formData.phone)) {
    newErrors.phone = "Phone must contain numbers only";
  }
  if (!formData.message.trim()) newErrors.message = "Message is required"; 
  // Opt-in
  if (!formData.optIn) {
    newErrors.optIn = "You must agree before submitting";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

// const handleSubmit = async (e) => {
//   e.preventDefault();
//   setIsSubmitting(true);
//   setIsSubmitted(true); // 👈 INSTANT SUCCESS UI
//   const formPayload = new FormData();
//   formPayload.append("name", formData.name);
//   formPayload.append("email", formData.email);
//   formPayload.append("company", formData.company);
//   formPayload.append("phone", `${formData.countryCode}${formData.phone}`);
//   formPayload.append("message", formData.message);

//   // 🔥 DO NOT await this
//   fetch("https://itmunch.com/b2b-lead-generation-service/send-mail.php", {
//     method: "POST",
//     body: formPayload,
//   }).catch(err => {
//     console.error("Background send failed", err);
//   });

//   // Reset form instantly
//   setFormData({
//     name: "",
//     email: "",
//     company: "",
//     countryCode: "+1",
//     phone:"",
//     message: "",
//     optIn: false,
//   });

//   setIsSubmitting(false);
// };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validate()) return;

  setIsSubmitting(true);
  setIsSubmitted(true);

  const formPayload = new FormData();
  formPayload.append("name", formData.name);
  formPayload.append("email", formData.email);
  formPayload.append("company", formData.company);
  formPayload.append("phone", `${formData.countryCode}${formData.phone}`);
  formPayload.append("message", formData.message);

  fetch("https://itmunch.com/b2b-lead-generation-services/send-mail.php", {
    method: "POST",
    body: formPayload,
  }).catch(err => console.error("Background send failed", err));

  setFormData({
    name: "",
    email: "",
    company: "",
    // countryCode: "+1",
    countryCode: "",
    phone: "",
    message: "",
    optIn: false,
  });

  setErrors({});
  setIsSubmitting(false);
};


  return (
    // <section className="relative pt-16 lg:pt-16 pb-16 lg:pb-16 overflow-hidden herrosection-pb">
    <section className="relative pt-16 lg:pt-16 overflow-hidden herrosection-pb">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white -z-10" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#4AC5DB]/10 to-transparent -z-10" />
      
      {/* Decorative Elements */}
      <div className="absolute top-40 left-10 w-72 h-72 bg-[#4AC5DB]/10 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#4AC5DB]/10 rounded-full blur-3xl -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 ">
          {/* Content */}
          <div className="text-center lg:text-left top-padding">
            <div className="inline-flex items-center gap-2 bg-[#4AC5DB]/10 text-[#4AC5DB] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star className="w-4 h-4 fill-[#4AC5DB] text-[#4AC5DB]" />
              Trusted by 500+ B2B Companies
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              {heroContent.headline.split('Quality').map((part, i) => 
                i === 0 ? (
                  <span key={i}>{part}<span className="text-[#4AC5DB]">Quality</span></span>
                ) : part
              )}
            </h1>
            
            <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
              {heroContent.subheadline}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <a href="https://calendly.com/simon-p-itmunch/30min" target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-[#4AC5DB] hover:bg-[#3bb5cb] text-white px-8 py-6 text-base"
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {heroContent.primaryCTA}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              </a>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-base"
                onClick={() => document.getElementById('process')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Play className="w-5 h-5 mr-2" />
                {heroContent.secondaryCTA}
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#4AC5DB]" />
                No setup fees
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#4AC5DB]" />
                Cancel anytime
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#4AC5DB]" />
                Results in 48hrs
              </div>
            </div>
          </div>

          {/* Lead Form */}
          <div id="contact-form" className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Get Your Free Strategy Session
            </h3>
            <p className="text-slate-600 mb-6">
              Fill out the form and we'll create a personalized lead generation plan for your business.
            </p>

            {isSubmitted ? (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-[#4AC5DB]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-[#4AC5DB]" />
                </div>
                <h4 className="text-xl font-semibold text-slate-900 mb-2">Thank You!</h4>
                <p className="text-slate-600">We'll be in touch within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
                    {/* <Input
                      type="text"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="h-12"
                    /> */}
                    
                    <Input
  type="text"
  placeholder="John Smith"
  value={formData.name}
  onChange={(e) => {
    const value = e.target.value;

    setFormData({ ...formData, name: value });

    // ✅ Remove error when user types something valid
    if (value.trim()) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.name;
        return newErrors;
      });
    }
  }}
  className={`h-12 ${
    errors.name ? "border-red-500 focus-visible:ring-red-500" : ""
  }`}
/>

                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Work Email</label>
                    {/* <Input
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="h-12"
                    /> */}
                    <Input
  type="email"
  placeholder="john@company.com"
  value={formData.email}
  onChange={(e) => {
    const value = e.target.value;
    setFormData({ ...formData, email: value });

    // Email validation logic
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const blockedDomains = ["gmail.com", "gmail.co.uk", "gmail.co.in",
"yahoo.com", "yahoo.co.in", "yahoo.co.uk", "yahoo.ca", "yahoo.com.au", "yahoo.com.sg", "yahoo.co.jp",
"outlook.com", "outlook.co.uk", "outlook.co.in",
"hotmail.com", "hotmail.co.uk",
"live.com", "live.co.uk",
"msn.com",
"icloud.com", "me.com", "mac.com",
"aol.com",
"proton.me", "protonmail.com",
"gmx.com", "gmx.de", "gmx.co.uk",
"mail.com",
"zoho.com",
"yandex.com", "yandex.ru",
"tutanota.com",
"fastmail.com",
"hushmail.com",
"rediffmail.com",
"inbox.com",
"rocketmail.com"];
    const domain = value.split("@")[1]?.toLowerCase();

    // ✅ Clear error only if valid company email
    if (
      emailRegex.test(value) &&
      domain &&
      !blockedDomains.includes(domain)
    ) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
    }
  }}
  className={`h-12 ${
    errors.email ? "border-red-500 focus-visible:ring-red-500" : ""
  }`}
/>

                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}

                  </div>
                </div>
                <div>
                  {/* <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Name</label> */}
                  <label className={`h-12 ${errors.company ? "border-red-500 focus-visible:ring-red-500" : ""}`}>Company Name</label>
                  <Input
                    type="text"
                    placeholder="Acme Inc."
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
                  <div className="flex flex-col gap-1">
  <div className="flex gap-2">
    <Popover open={countryOpen} onOpenChange={setCountryOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={countryOpen}
          className={`w-[140px] h-12 justify-between font-normal ${
            errors.countryCode ? "border-red-500 focus-visible:ring-red-500" : ""
          }`}
        >
          {selectedCountry ? (
            <span className="flex items-center gap-2">
              <span>{selectedCountry.flag}</span>
              <span>{selectedCountry.code}</span>
            </span>
          ) : (
            "Select..."
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[250px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countryCodes.map((country) => (
                <CommandItem
                  key={`${country.code}-${country.country}`}
                  value={country.country}
                  onSelect={() => {
  setFormData({...formData, countryCode: country.code});
  setCountryOpen(false);

  // ✅ Remove countryCode error instantly
  setErrors(prev => {
    const newErrors = {...prev};
    delete newErrors.countryCode;
    return newErrors;
  });
}}

                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      formData.countryCode === country.code
                        ? "opacity-100"
                        : "opacity-0"
                    }`}
                  />
                  <span className="mr-2">{country.flag}</span>
                  <span className="flex-1">{country.country}</span>
                  <span className="text-slate-500">{country.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>

    <Input
  type="tel"
  placeholder="555 000-0000"
  value={formData.phone}
  onChange={(e) => {
    const onlyNums = e.target.value.replace(/\D/g, "");
    setFormData({ ...formData, phone: onlyNums });

    // ✅ Clear error if valid number entered
    if (onlyNums.trim() && /^\d+$/.test(onlyNums)) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.phone;
        return newErrors;
      });
    }
  }}
  className={`h-12 flex-1 ${
    errors.phone ? "border-red-500 focus-visible:ring-red-500" : ""
  }`}
/>

  </div>

  {errors.countryCode && (
    <p className="text-red-500 text-xs">{errors.countryCode}</p>
  )}

  {errors.phone && (
    <p className="text-red-500 text-xs">{errors.phone}</p>
  )}
</div>

                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Message</label>
                  {/* <Textarea
                    placeholder="Tell us about your lead generation needs..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="min-h-[100px] resize-none"
                  /> */}
                  <textarea
  placeholder="Tell us about your lead generation needs..."
  value={formData.message}
  onChange={(e) => {
    setFormData({ ...formData, message: e.target.value });

    // Clear error as soon as user types
    if (e.target.value.trim()) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.message;
        return newErrors;
      });
    }
  }}
  className={`w-full p-3 border rounded-md ${
    errors.message ? "border-red-500 focus-visible:ring-red-500" : "border-gray-300"
  }`}
  rows={4}
/>
{errors.message && (
  <p className="text-red-500 text-xs mt-1">{errors.message}</p>
)}

                </div>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="optIn"
                    checked={formData.optIn}
                    onCheckedChange={(checked) => setFormData({...formData, optIn: checked})}
                    className="mt-1 data-[state=checked]:bg-[#4AC5DB] data-[state=checked]:border-[#4AC5DB]"
                  />
                  <label htmlFor="optIn" className="text-sm text-slate-600 cursor-pointer">
                    I agree to receive marketing communications from iTMunch and understand I can unsubscribe at any time.
                  </label>
                </div>
                <Button
  type="submit"
  disabled={isSubmitting || !formData.optIn}
  className="w-full h-12 bg-[#4AC5DB] text-white"
>
  {isSubmitting ? "Submitting..." : "Get Started Now"}
</Button>

                <p className="text-xs text-slate-500 text-center">
                  By submitting, you agree to our{' '}
                  <a 
                    href="https://itmunch.com/privacy-policy/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#4AC5DB] hover:underline"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a 
                    href="https://itmunch.com/terms-conditions/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#4AC5DB] hover:underline"
                  >
                    Terms & Conditions
                  </a>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Client Logos Section
const ClientLogos = () => (
  <section className="py-12 border-y border-slate-100 bg-slate-50/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-center text-sm font-medium text-slate-500 mb-8">
        TRUSTED BY LEADING B2B COMPANIES WORLDWIDE
      </p>
      <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
        {clientLogos.map((client) => (
          <div 
            key={client.id} 
            className="h-8 px-4 flex items-center justify-center text-slate-400 font-bold text-lg tracking-wide hover:text-slate-600 transition-colors"
          >
            {client.name}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Stats Section
const StatsSection = () => (
  // <section className="py-16 lg:py-24 bg-slate-900">
  <section className="py-16 lg:py-16 bg-slate-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
        {stats.map((stat) => (
          <div key={stat.id} className="text-center">
            <div className="text-4xl lg:text-5xl font-bold text-white mb-2">
              <AnimatedCounter value={stat.value} suffix={stat.suffix} />
            </div>
            <p className="text-slate-400 font-medium">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// What Is Lead Gen Section
const WhatIsSection = () => (
  // <section id="services" className="py-16 lg:py-24">
  <section id="services" className="py-16 ">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <span className="inline-block text-[#4AC5DB] font-semibold text-sm uppercase tracking-wider mb-4">
            Understanding Lead Generation
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
            {whatIsLeadGen.title}
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            {whatIsLeadGen.content.split('\n\n').map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
          <Button 
            className="mt-8 bg-[#4AC5DB] hover:bg-[#3bb5cb] text-white"
            onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Start Generating Leads
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        <div className="relative">
          <div className="bg-gradient-to-br from-[#4AC5DB]/10 to-[#4AC5DB]/5 rounded-2xl p-8 lg:p-12">
            <div className="space-y-6">
              {['Content Marketing', 'Email Outreach', 'LinkedIn Prospecting', 'Paid Advertising', 'Strategic Partnerships'].map((item, i) => (
                <div key={i} className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-[#4AC5DB]/20 flex items-center justify-center text-[#4AC5DB] font-bold">
                    {i + 1}
                  </div>
                  <span className="font-medium text-slate-800">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#4AC5DB]/10 rounded-full blur-2xl" />
        </div>
      </div>
    </div>
  </section>
);

// Benefits Section
const BenefitsSection = () => (
  <section className="py-16 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="inline-block text-[#4AC5DB] font-semibold text-sm uppercase tracking-wider mb-4">
          Why Lead Generation Matters
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
          Benefits That Drive Real Business Growth
        </h2>
        <p className="text-lg text-slate-600">
          Effective lead generation isn't just about volume—it's about connecting with the right prospects at the right time.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {benefits.map((benefit) => {
          const Icon = iconMap[benefit.icon];
          return (
            <div 
              key={benefit.id} 
              className="bg-white rounded-xl p-6 lg:p-8 shadow-sm hover:shadow-lg transition-shadow duration-300 border border-slate-100 group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#4AC5DB]/10 flex items-center justify-center mb-5 group-hover:bg-[#4AC5DB]/20 transition-colors">
                <Icon className="w-6 h-6 text-[#4AC5DB]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                {benefit.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Why Choose Us Section
const WhyChooseUsSection = () => (
  <section id="why-us" className="py-16 pt-2rem">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="inline-block text-[#4AC5DB] font-semibold text-sm uppercase tracking-wider mb-4">
          Why Choose {companyInfo.name}
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
          What Sets Us Apart From the Rest
        </h2>
        <p className="text-lg text-slate-600">
          We combine cutting-edge technology with deep industry expertise to deliver leads that actually convert.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {whyChooseUs.map((feature) => {
          const Icon = iconMap[feature.icon];
          return (
            <div 
              key={feature.id} 
              className="flex gap-6 p-6 lg:p-8 rounded-xl border border-slate-200 hover:border-[#4AC5DB]/50 hover:bg-[#4AC5DB]/5 transition-all duration-300"
            >
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-xl bg-[#4AC5DB]/20 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-[#4AC5DB]" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Process Section
const ProcessSection = () => (
  <section id="process" className="py-16 bg-slate-900">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <span className="inline-block text-[#4AC5DB] font-semibold text-sm uppercase tracking-wider mb-4">
          Our Process
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
          How Our B2B Lead Generation Works
        </h2>
        <p className="text-lg text-slate-400">
          A systematic approach to connecting you with high-quality prospects.
        </p>
      </div>

      <div className="relative">
        {/* Connection Line */}
        <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-slate-700 -translate-y-1/2" />
        
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
          {processSteps.map((step, index) => (
            <div key={step.id} className="relative">
              <div className="bg-slate-800 rounded-xl p-6 h-full hover:bg-slate-800/80 transition-colors">
                <div className="w-12 h-12 rounded-full bg-[#4AC5DB] flex items-center justify-center text-white font-bold text-lg mb-4 relative z-10">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-center mt-12">
        <Button 
          size="lg" 
          className="bg-[#4AC5DB] hover:bg-[#3bb5cb] text-white px-8"
          onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
        >
          Start Your Campaign
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  </section>
);

// Testimonials Section
const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="testimonials" className="py-16  bg-[#4AC5DB]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-[#4AC5DB] font-semibold text-sm uppercase tracking-wider mb-4">
            Client Success Stories
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-slate-600">
            Real results from real businesses that transformed their sales pipeline.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 relative">
            <Quote className="absolute top-8 left-8 w-12 h-12 text-[#4AC5DB]/20" />
            <div className="relative z-10">
              <p className="text-xl lg:text-2xl text-slate-700 leading-relaxed mb-8 italic">
                "{testimonials[activeIndex].quote}"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#4AC5DB]/20 flex items-center justify-center text-[#4AC5DB] font-bold text-lg">
                  {testimonials[activeIndex].author.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">
                    {testimonials[activeIndex].author}
                  </p>
                  <p className="text-sm text-slate-500">
                    {testimonials[activeIndex].role}, {testimonials[activeIndex].company}
                  </p>
                </div>
                <div className="ml-auto hidden sm:block">
                  <span className="inline-block px-3 py-1 bg-[#4AC5DB]/10 text-[#4AC5DB] rounded-full text-sm font-medium">
                    {testimonials[activeIndex].industry}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === activeIndex 
                    ? 'bg-[#4AC5DB] w-8' 
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => (
  <section id="faq" className="py-16 ">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <span className="inline-block text-[#4AC5DB] font-semibold text-sm uppercase tracking-wider mb-4">
          Have Questions?
        </span>
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-lg text-slate-600">
          Everything you need to know about our B2B lead generation services.
        </p>
      </div>

      <Accordion type="single" collapsible className="space-y-4">
        {faqs.map((faq) => (
          <AccordionItem 
            key={faq.id} 
            value={`item-${faq.id}`}
            className="bg-white rounded-xl border border-slate-200 px-6 data-[state=open]:shadow-lg data-[state=open]:border-[#4AC5DB]/30 transition-shadow"
          >
            <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[#4AC5DB] py-5 [&[data-state=open]>svg]:rotate-180">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
        {ctaContent.headline}
      </h2>
      <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
        {ctaContent.subheadline}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button 
          size="lg" 
          className="bg-[#4AC5DB] hover:bg-[#3bb5cb] text-white px-10 py-6 text-lg font-semibold"
          onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
        >
          {ctaContent.buttonText}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        <a href="https://calendly.com/simon-p-itmunch/30min" target="_blank" rel="noopener noreferrer">
        <Button 
          size="lg" 
          variant="outline" 
          className="border-slate-500 text-white hover:bg-slate-800 px-10 py-6 text-lg"
        >
          <Phone className="w-5 h-5 mr-2" />
          Schedule a Call
        </Button>
        </a>
      </div>
    </div>
  </section>
);

// Footer Component
const Footer = () => (
  <footer className="bg-slate-950 pt-16 pb-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
        {/* Brand */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <img 
              src={companyInfo.logo.footer} 
              alt={companyInfo.name}
              className="h-20 sh-copyrights-image"
            />
          </div>
          <p className="text-slate-400 mb-6 max-w-sm">
            {companyInfo.description}
          </p>
          <div className="flex gap-4">
            <a href="https://www.linkedin.com/company/itmunch/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#4AC5DB] hover:text-white transition-colors">
              <Linkedin className="w-5 h-5" />
            </a>
            <a
  href="https://x.com/aboraboraborab"
  target="_blank"
  rel="noopener noreferrer"
  className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#4AC5DB] hover:text-white transition-colors"
>
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="w-5 h-5 fill-current"
  >
    <path d="M18.244 2H21.5l-7.52 8.59L22.5 22h-6.77l-5.3-6.63L4.3 22H1l8.06-9.2L1.5 2h6.94l4.8 6.02L18.244 2Zm-1.19 18h1.87L6.02 4H4.02l13.03 16Z" />
  </svg>
</a>
            <a
  href="https://www.facebook.com/iTMunch"
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Follow us on Facebook"
  title="Facebook"
  className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#4AC5DB] hover:text-white transition-colors"
>
  <Facebook className="w-5 h-5" />
</a>
<a
  href="https://www.instagram.com/it_munch/"
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Follow us on Instagram"
  title="Instagram"
  className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#E1306C] hover:text-white transition-colors"
>
  <Instagram className="w-5 h-5" />
</a>
<a
  href="https://www.youtube.com/@itmunchnews9843"
  target="_blank"
  rel="noopener noreferrer"
  aria-label="Subscribe to our YouTube channel"
  title="YouTube"
  className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#FF0000] hover:text-white transition-colors"
>
  <Youtube className="w-5 h-5" />
</a>
          </div>
        </div>

        {/* Services Links */}
        <div>
          <h4 className="font-semibold text-white mb-4">Services</h4>
          <ul className="space-y-3">
            {footerLinks.services.map((link) => (
              <li key={link.label}>
                <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#4AC5DB] transition-colors">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Company Links */}
        <div>
          <h4 className="font-semibold text-white mb-4">Company</h4>
          <ul className="space-y-3">
            {footerLinks.company.map((link) => (
              <li key={link.label}>
                <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#4AC5DB] transition-colors">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Partner Websites */}
        <div>
          <h4 className="font-semibold text-white mb-4">Partner Websites</h4>
          <ul className="space-y-3">
            {footerLinks.partners.map((link) => (
              <li key={link.label}>
                <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#4AC5DB] transition-colors">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-semibold text-white mb-4">Contact</h4>
          <ul className="space-y-3 text-slate-400">
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#4AC5DB]" />
              <a href="mailto:hello@itmunch.com" className="hover:text-[#4AC5DB] transition-colors">
                hello@itmunch.com
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#4AC5DB]" />
              <a href="https://itmunch.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#4AC5DB] transition-colors">
                itmunch.com
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-slate-500 text-sm">
          © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
        </p>
        <div className="flex gap-6 text-sm text-slate-500">
          {footerLinks.legal.map((link) => (
            <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="hover:text-[#4AC5DB] transition-colors">
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <HeroSection />
        <ClientLogos />
        <StatsSection />
        <WhatIsSection />
        <BenefitsSection />
        <WhyChooseUsSection />
        <ProcessSection />
        <TestimonialsSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
